// src/profile-tasks/CallConsole.jsx
//
// Live Call Task Console — Build Prompt §3. Mounts on the lead /
// client profile. The operator types fast shorthand while on the
// phone, the AI parses, every task lands in the optimistic feed,
// and contact tasks dispatch through the existing SHIELD stack
// (Phase 1 gate; Phase 2 parser).
//
//   Command box — always-focused, monospace. Enter submits,
//                 Shift+Enter newlines, `/` re-focuses from anywhere.
//                 Re-focuses immediately after submit so the agent
//                 never loses the cursor mid-call.
//   Quick chips — six pre-typed task templates; no AI round-trip.
//   Task feed  — every queued task, with type tag, agent, priority,
//                compliance flag, due / scheduled_for, action btns.
//   ON CALL    — badge surfaces when the parent passes inCall=true.
//
// All sends route through /api/profile-tasks/dispatch which runs
// the SHIELD gate. Parsing through /api/profile-tasks/parse never
// reaches a provider — capture is free, execution is gated (§2).

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Sparkles, Phone, MessageSquare, Mail, FileText, CalendarPlus, Shield, AlertTriangle, X, Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, init = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(body?.error || `HTTP ${res.status}`);
    e.status = res.status; e.body = body;
    throw e;
  }
  return body;
}

// ── Quick-action chip templates — Build Prompt §3 ──────────────
// Each chip POSTs straight to /api/profile-tasks with a pre-shaped
// task so the panel never has to round-trip the AI for the most-
// repeated patterns.
const QUICK_CHIPS = [
  { key: 'soa',        label: 'Send SOA',        icon: Shield,        title: 'Send Scope of Appointment',                  task_type: 'compliance', agent: 'SHIELD', line_of_business: 'medicare', priority: 'high' },
  { key: 'quote',      label: 'Send quote',      icon: FileText,      title: 'Send quote / illustration',                   task_type: 'document',   agent: 'ENROLL', priority: 'normal' },
  { key: 'callback',   label: 'Callback',        icon: CalendarPlus,  title: 'Callback (time TBD)',                         task_type: 'schedule',   agent: 'you',    priority: 'high' },
  { key: 'nurture',    label: 'Add to nurture',  icon: MessageSquare, title: 'Add to NUDGE follow-up cadence',              task_type: 'nurture',    agent: 'NOVA',   priority: 'normal' },
  { key: 'eligibility',label: 'Run eligibility', icon: CheckCircle2,  title: 'Run ACA / Medicare eligibility + subsidy check', task_type: 'note',    agent: 'ENROLL', priority: 'high' },
  { key: 'objection',  label: 'Log objection',   icon: AlertTriangle, title: 'Log objection / concern',                     task_type: 'note',       agent: 'you',    priority: 'normal' },
];

// ── Task type styling ─────────────────────────────────────────
const TYPE_META = {
  call:       { icon: Phone,        tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  sms:        { icon: MessageSquare,tone: 'bg-sky-50 text-sky-800 border-sky-200' },
  email:      { icon: Mail,         tone: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
  document:   { icon: FileText,     tone: 'bg-violet-50 text-violet-800 border-violet-200' },
  schedule:   { icon: CalendarPlus, tone: 'bg-amber-50 text-amber-800 border-amber-200' },
  note:       { icon: FileText,     tone: 'bg-slate-50 text-slate-700 border-slate-200' },
  compliance: { icon: Shield,       tone: 'bg-red-50 text-red-800 border-red-200' },
  nurture:    { icon: Sparkles,     tone: 'bg-violet-50 text-violet-800 border-violet-200' },
};
const STATUS_PILL = {
  pending_parse: 'bg-slate-100 text-slate-600 border-slate-200',
  queued:        'bg-amber-50 text-amber-800 border-amber-200',
  blocked:       'bg-red-50 text-red-800 border-red-200',
  scheduled:     'bg-sky-50 text-sky-800 border-sky-200',
  done:          'bg-emerald-50 text-emerald-800 border-emerald-200',
  cancelled:     'bg-slate-100 text-slate-500 border-slate-200',
};
const PRIORITY_DOT = { low: 'bg-slate-300', normal: 'bg-slate-400', high: 'bg-amber-500', urgent: 'bg-red-500' };

function fmtRel(iso) {
  if (!iso) return '';
  const t = Date.parse(iso); if (!Number.isFinite(t)) return '';
  const mins = Math.round((Date.now() - t) / 60_000);
  if (mins < -1440) return `in ${Math.round(Math.abs(mins) / 1440)}d`;
  if (mins < -60)   return `in ${Math.round(Math.abs(mins) / 60)}h`;
  if (mins < 0)     return `in ${Math.abs(mins)}m`;
  if (mins < 1)     return 'now';
  if (mins < 60)    return `${mins}m ago`;
  if (mins < 1440)  return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

export default function CallConsole({ prospectId, prospectName, clientContext = {}, inCall = false, showToast = () => {}, onClose }) {
  const [tasks, setTasks]       = React.useState([]);
  const [loading, setLoading]   = React.useState(true);
  const [draft, setDraft]       = React.useState('');
  const [sending, setSending]   = React.useState(false);
  const inputRef = React.useRef(null);

  const load = React.useCallback(async () => {
    if (!prospectId) return;
    setLoading(true);
    try {
      const r = await apiFetch(`/api/profile-tasks?prospect_id=${encodeURIComponent(prospectId)}&limit=200`);
      setTasks(r.tasks || []);
    } catch (err) {
      showToast(`Tasks load failed: ${err.message || err}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [prospectId, showToast]);

  React.useEffect(() => { load(); }, [load]);

  // Always-focused command box. Re-focuses after submit so the
  // agent never loses the cursor mid-call. Also re-focuses when
  // the operator hits `/` from anywhere in the modal.
  React.useEffect(() => {
    function onKey(e) {
      if (e.key === '/' && document.activeElement !== inputRef.current) {
        const tag = String(document.activeElement?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea') return;
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  React.useEffect(() => { inputRef.current?.focus(); }, []);

  async function submitDraft() {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);

    // Optimistic pending row at the top of the feed. If parse
    // succeeds we replace it with the structured tasks the parser
    // returned; if parse fails we leave it as a manual note so the
    // operator's text is never lost (§4 optimistic UX).
    const optimisticId = `opt_${Math.random().toString(36).slice(2, 10)}`;
    const optimistic = {
      id: optimisticId,
      title: text,
      task_type: 'note',
      status: 'pending_parse',
      raw_text: text,
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    setTasks(prev => [optimistic, ...prev]);
    setDraft('');
    inputRef.current?.focus();

    try {
      const r = await apiFetch('/api/profile-tasks/parse', {
        method: 'POST',
        body:   JSON.stringify({
          raw_text:    text,
          prospect_id: prospectId,
          context:     {
            name:              prospectName || clientContext.name || null,
            lines_of_business: clientContext.lines_of_business || [],
            consents_on_file:  clientContext.consents_on_file || {},
            timezone:          clientContext.timezone || null,
          },
        }),
      });
      setTasks(prev => {
        const without = prev.filter(t => t.id !== optimisticId);
        return [...(r.tasks || []), ...without];
      });
    } catch (err) {
      // Leave the optimistic row in place but flag it as manual.
      setTasks(prev => prev.map(t => t.id === optimisticId
        ? { ...t, status: 'queued', _manual: true }
        : t));
      showToast(`Parse failed — kept as manual note: ${err.message || err}`, 'info');
    } finally {
      setSending(false);
    }
  }

  async function createFromChip(chip) {
    setTasks(prev => [{
      id: `opt_${Math.random().toString(36).slice(2, 10)}`,
      title: chip.title,
      task_type: chip.task_type,
      agent: chip.agent || null,
      line_of_business: chip.line_of_business || null,
      priority: chip.priority || 'normal',
      status: 'queued',
      created_at: new Date().toISOString(),
      _optimistic: true,
    }, ...prev]);
    try {
      const r = await apiFetch('/api/profile-tasks', {
        method: 'POST',
        body:   JSON.stringify({
          prospect_id:      prospectId,
          title:            chip.title,
          task_type:        chip.task_type,
          agent:            chip.agent || null,
          line_of_business: chip.line_of_business || null,
          priority:         chip.priority || 'normal',
        }),
      });
      setTasks(prev => [r.task, ...prev.filter(t => !t._optimistic)]);
    } catch (err) {
      showToast(`Couldn't create task: ${err.message || err}`, 'error');
      setTasks(prev => prev.filter(t => !t._optimistic));
    }
  }

  async function dispatchTask(task) {
    if (!task || task.status === 'done' || task._optimistic) return;
    try {
      const r = await apiFetch('/api/profile-tasks/dispatch', {
        method: 'POST',
        body:   JSON.stringify({ id: task.id }),
      });
      // Replace the row with the returned task (status updated +
      // blockers populated if the gate refused).
      setTasks(prev => prev.map(t => t.id === task.id ? r.task : t));
      if (r.blocked) {
        showToast(`Blocked: ${(r.blockers || []).join(', ')}`, 'info');
      } else if (r.dispatched) {
        showToast('Dispatched ✓', 'success');
      }
    } catch (err) {
      showToast(`Dispatch failed: ${err.message || err}`, 'error');
    }
  }

  async function removeTask(task) {
    if (!task) return;
    if (task._optimistic) {
      setTasks(prev => prev.filter(t => t.id !== task.id));
      return;
    }
    if (!(await (window.hqConfirm ? window.hqConfirm({
      title: `Remove "${task.title}"?`,
      confirmLabel: 'Remove task', danger: true,
    }) : Promise.resolve(window.confirm(`Remove "${task.title}"?`))))) return;
    try {
      await apiFetch(`/api/profile-tasks?id=${encodeURIComponent(task.id)}`, { method: 'DELETE' });
      setTasks(prev => prev.filter(t => t.id !== task.id));
    } catch (err) {
      showToast(`Remove failed: ${err.message || err}`, 'error');
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Task the AI</span>
          {inCall && (
            <span className="text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/> ON CALL
            </span>
          )}
        </div>
        {onClose && (
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 text-lg leading-none">×</button>
        )}
      </div>

      {/* Command box */}
      <form onSubmit={e => { e.preventDefault(); submitDraft(); }} className="space-y-2">
        <textarea
          ref={inputRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submitDraft();
            }
          }}
          disabled={sending}
          placeholder="Type fast, hit enter, keep talking… e.g. cb 8pm + text fe quote + note overweight wants coverage, also aca + dental"
          rows={2}
          className="w-full resize-none border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:border-violet-500 disabled:bg-slate-50"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="submit"
            disabled={sending || !draft.trim()}
            className="bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white font-bold text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5"
          >
            {sending ? <><Loader2 size={12} className="animate-spin"/> Parsing…</> : <><Sparkles size={12}/> Send to AI</>}
          </button>
          <span className="text-[10px] text-slate-400">Enter to send · Shift+Enter newline · / to refocus</span>
        </div>
      </form>

      {/* Quick chips */}
      <div className="flex items-center gap-1.5 flex-wrap pt-2 border-t border-slate-100">
        {QUICK_CHIPS.map(chip => {
          const Icon = chip.icon;
          return (
            <button
              key={chip.key}
              onClick={() => createFromChip(chip)}
              className="text-[11px] font-semibold bg-slate-50 hover:bg-violet-50 border border-slate-200 hover:border-violet-300 text-slate-700 hover:text-violet-700 rounded-full px-2 py-1 inline-flex items-center gap-1"
              title={chip.title}
            >
              <Icon size={11}/> {chip.label}
            </button>
          );
        })}
      </div>

      {/* Feed */}
      <div className="pt-2 border-t border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Tasks · {tasks.length}</span>
          <button onClick={load} className="text-[10px] text-slate-400 hover:text-slate-700">Refresh</button>
        </div>
        {loading && tasks.length === 0 ? (
          <div className="text-xs text-slate-500 text-center py-4"><Loader2 size={14} className="animate-spin inline mr-2"/>Loading…</div>
        ) : tasks.length === 0 ? (
          <div className="text-xs text-slate-500 text-center py-6 bg-slate-50 border border-slate-200 rounded-lg">
            No tasks yet. Type a note or tap a chip.
          </div>
        ) : (
          <div className="space-y-2">
            {tasks.map(t => (
              <TaskRow key={t.id} task={t} onDispatch={() => dispatchTask(t)} onRemove={() => removeTask(t)}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskRow({ task, onDispatch, onRemove }) {
  const typeMeta = TYPE_META[task.task_type] || TYPE_META.note;
  const TypeIcon = typeMeta.icon;
  const dispatchable = !task._optimistic && !['done','cancelled'].includes(task.status);
  const blockers = Array.isArray(task.blockers) ? task.blockers : (task.blockers ? [task.blockers] : []);
  return (
    <div className={`border rounded-lg p-2 ${task._optimistic ? 'border-dashed border-slate-300 bg-slate-50' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-start gap-2">
        <div className={`flex items-center justify-center w-7 h-7 rounded border ${typeMeta.tone} shrink-0 mt-0.5`}>
          <TypeIcon size={13}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-semibold text-slate-900 truncate">{task.title || '(untitled)'}</span>
            <span className={`w-1.5 h-1.5 rounded-full ${PRIORITY_DOT[task.priority] || 'bg-slate-400'}`} title={`priority: ${task.priority}`}/>
            <span className={`text-[9px] uppercase tracking-wider font-bold border rounded px-1 py-0.5 ${STATUS_PILL[task.status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
              {(task.status || 'queued').replace(/_/g, ' ')}
            </span>
            {task.agent && (
              <span className="text-[9px] uppercase tracking-wider font-bold bg-slate-100 text-slate-700 border border-slate-200 rounded px-1 py-0.5">{task.agent}</span>
            )}
            {task.line_of_business && (
              <span className="text-[9px] uppercase tracking-wider font-bold bg-slate-50 text-slate-600 border border-slate-200 rounded px-1 py-0.5">{task.line_of_business.replace(/_/g,'+')}</span>
            )}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5 flex gap-2 flex-wrap">
            <span>{fmtRel(task.created_at)}</span>
            {task.scheduled_for && <span>· scheduled {fmtRel(task.scheduled_for)}</span>}
            {task.compliance_flag && <span>· ⚠ {task.compliance_flag}</span>}
          </div>
          {blockers.length > 0 && (
            <div className="text-[10px] text-red-700 mt-1 leading-snug bg-red-50 border border-red-200 rounded px-2 py-1">
              <strong>Blocked:</strong> {blockers.join(', ')}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {dispatchable && (
            <button onClick={onDispatch} className="text-[10px] font-bold text-emerald-700 hover:text-emerald-900 px-1.5 py-1" title="Run through SHIELD gate">▶ Run</button>
          )}
          <button onClick={onRemove} className="text-slate-400 hover:text-red-600 text-sm leading-none px-1" title="Remove"><X size={12}/></button>
        </div>
      </div>
    </div>
  );
}
